import React, { useId, useMemo, useState } from 'react';
import { Input } from 'antd';

import { IDENTITY_GLYPH_COLORS } from '../../config/brand';
import { identityGlyphColor, parseColor } from '../../utils/colors';
import { contrastRatio } from '../../utils/color/paletteChecks';
import {
	DEFAULT_HSB, clamp, hsbFromRgb, hsbToHex, hsbToRgb, parseChannelInput, parseHexInput, rgbToHex, rgbToHsb,
} from '../../utils/color/colorModels';
import { grayStripCells, honeycombCells } from '../../utils/color/honeycomb';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import Honeycomb from './Honeycomb';
import { RecordIconTile } from './RecordBadge';

/**
 * The custom color panel of the icon + color picker (KPR-314): any color,
 * chosen four ways that stay in step, a honeycomb to click, hue / saturation /
 * brightness sliders, and typed Hex, R G B and H S B, with the icon on the
 * color as a live preview.
 *
 * Controlled by `color` (any CSS color the app has stored; read through
 * utils/colors.js parseColor) and `onChange(hex)`, which is always a lower-case
 * "#rrggbb": the spelling elementFormat/color.js stores, so nothing about the
 * contract moves. It is called only when the color really changes, so opening
 * the panel on an item with no color chooses nothing.
 *
 * The panel keeps its own HSB as the truth, not the hex. A hex cannot say
 * which hue a gray had, so a picker built on hex snaps its hue slider to red
 * the moment saturation hits 0, and typing H / S / B would drift through
 * 8-bit rounding. When the `color` prop changes to a color the panel is not
 * already showing (a palette swatch was clicked), it takes that one.
 *
 * Typed fields keep their own text while they are being edited. A valid
 * entry applies at once and every other field follows; an invalid one is
 * flagged in place and the last valid color stays: the color never goes to a
 * half-typed "#2f6" or an out-of-range 300. Leaving the field (or Escape)
 * puts the last valid value back in it.
 */

// Hue slider's rainbow, in 60 degree stops; the track is the same whatever
// else is chosen, so it always says where on the wheel the thumb is.
const HUE_STOPS = [0, 60, 120, 180, 240, 300, 360];
const GRAYS = grayStripCells();

const PHONE_RINGS = 3; // 7 cells across a 327px sheet is 46px each: over the 44px touch floor.
const DESKTOP_RINGS = 6; // 127 cells.

const ratioText = ratio => `${(Math.floor(ratio * 10) / 10).toFixed(1)}:1`;

/** One typed field: Hex, a channel, or an HSB value. See the header for how it behaves. */
function TypedField({ id, label, ariaLabel, value, parse, onCommit, problem, problemId, setProblem, problemText, step = 0, max = 0, className = '', ...rest }) {
	const [draft, setDraft] = useState(null);
	const invalid = problem?.id === id;

	const settle = () => {
		setDraft(null);
		setProblem(current => (current?.id === id ? null : current));
	};

	const handleChange = event => {
		const text = event.target.value;
		setDraft(text);
		const parsed = parse(text);
		if (parsed === null) {
			setProblem({ id, text: problemText });
			return;
		}
		setProblem(current => (current?.id === id ? null : current));
		onCommit(parsed);
	};

	const handleKeyDown = event => {
		if (event.key === 'Escape' && draft !== null) {
			// Reverting a half-typed entry must not also close the modal around it.
			event.stopPropagation();
			settle();
			return;
		}
		if (step && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
			event.preventDefault();
			const now = parse(draft ?? `${value}`);
			const base = now === null ? value : now;
			const delta = (event.shiftKey ? 10 : 1) * (event.key === 'ArrowUp' ? 1 : -1);
			setDraft(null);
			setProblem(current => (current?.id === id ? null : current));
			onCommit(clamp(base + delta, 0, max));
		}
	};

	return (
		<label className={`custom-color__field-label ${className}`.trim()} htmlFor={id}>
			<span className='custom-color__field-name' aria-hidden='true'>{label}</span>
			<Input
				{...NO_TEXT_ASSIST}
				{...rest}
				id={id}
				className='custom-color__input'
				value={draft ?? `${value}`}
				onChange={handleChange}
				onBlur={settle}
				onKeyDown={handleKeyDown}
				aria-label={ariaLabel}
				aria-invalid={invalid}
				aria-describedby={invalid ? problemId : undefined}
				status={invalid ? 'error' : undefined}
				inputMode={rest.inputMode || 'numeric'}
				spellCheck={false}
			/>
		</label>
	);
}

export default function CustomColorPicker({
	color,
	onChange,
	icon = null,
	recordKind = 'card',
	phone = false,
	id,
	className = '',
}) {
	const uid = useId();
	const field = name => `${uid}-${name}`;
	const problemId = field('problem');

	const parsedProp = useMemo(() => parseColor(color), [color]);
	const propHex = parsedProp ? rgbToHex(parsedProp) : null;

	const [hsb, setHsb] = useState(() => (parsedProp ? rgbToHsb(parsedProp) : { ...DEFAULT_HSB }));
	const [problem, setProblem] = useState(null);

	const rgb = hsbToRgb(hsb);
	const hex = rgbToHex(rgb);

	// A color arriving from outside (a palette swatch, a reopened modal) that
	// the panel is not already showing becomes the panel's. Adjusted while
	// rendering, not in an effect, so the panel is never painted for a frame
	// showing the old color beside a preview that shows the new one.
	const [seenHex, setSeenHex] = useState(propHex);
	if (propHex !== seenHex) {
		setSeenHex(propHex);
		if (propHex && propHex !== hex) setHsb(hsbFromRgb(parsedProp, hsb));
	}

	const commit = next => {
		setHsb(next);
		const nextHex = hsbToHex(next);
		if (nextHex !== propHex) onChange(nextHex);
	};

	const commitRgb = next => {
		// Typing the color already shown must not replace the exact HSB with a
		// rounded copy of itself. Unless nothing is chosen yet: then the shown
		// default is not a choice, and typing it is one.
		if (propHex && rgbToHex(next) === hex) return;
		commit(hsbFromRgb(next, hsb));
	};

	const rings = phone ? PHONE_RINGS : DESKTOP_RINGS;
	const comb = useMemo(() => honeycombCells(rings), [rings]);

	const glyph = identityGlyphColor(hex);
	const ratio = contrastRatio(hex, glyph);

	const hueTrack = `linear-gradient(to right, ${HUE_STOPS.map(h => hsbToHex({ h, s: 100, b: 100 })).join(', ')})`;
	const saturationTrack = `linear-gradient(to right, ${hsbToHex({ ...hsb, s: 0 })}, ${hsbToHex({ ...hsb, s: 100 })})`;
	const brightnessTrack = `linear-gradient(to right, ${hsbToHex({ ...hsb, b: 0 })}, ${hsbToHex({ ...hsb, b: 100 })})`;

	const hsbRow = (key, name, max, track) => (
		<div className='custom-color__slider-row' key={key}>
			<span className='custom-color__slider-name' aria-hidden='true'>{name}</span>
			<input
				type='range'
				className='custom-color__slider'
				style={{ '--track': track }}
				min={0}
				max={max}
				step={1}
				value={Math.round(hsb[key])}
				onChange={event => commit({ ...hsb, [key]: Number(event.target.value) })}
				aria-label={name}
			/>
			<TypedField
				id={field(`${key}-value`)}
				label={name[0]}
				ariaLabel={`${name} value`}
				className='custom-color__hsb-field'
				value={Math.round(hsb[key])}
				parse={text => parseChannelInput(text, max)}
				onCommit={value => commit({ ...hsb, [key]: value })}
				problem={problem}
				problemId={problemId}
				setProblem={setProblem}
				problemText={`${name} needs a whole number from 0 to ${max}. Keeping ${hex}.`}
				step={1}
				max={max}
				maxLength={3}
			/>
		</div>
	);

	const channel = (key, name) => (
		<TypedField
			key={key}
			id={field(`${key}-value`)}
			label={key.toUpperCase()}
			ariaLabel={name}
			value={rgb[key]}
			parse={text => parseChannelInput(text, 255)}
			onCommit={value => commitRgb({ ...rgb, [key]: value })}
			problem={problem}
			problemId={problemId}
			setProblem={setProblem}
			problemText={`${name} needs a whole number from 0 to 255. Keeping ${hex}.`}
			step={1}
			max={255}
			maxLength={3}
		/>
	);

	return (
		<div
			id={id}
			className={`custom-color${phone ? ' custom-color--phone' : ''} ${className}`.trim()}
			role='group'
			aria-label='Custom color picker'
		>
			<div className='custom-color__field'>
				<Honeycomb
					layout={comb}
					label='Honeycomb of colors'
					selectedHex={hex}
					onPick={cell => commit(cell.hsb)}
					className='custom-color__honeycomb'
				/>
				<Honeycomb
					layout={GRAYS}
					label='Grays'
					selectedHex={hex}
					onPick={cell => commit(cell.hsb)}
					className='custom-color__grays'
					// The same cell size as the honeycomb above: 7 cells of its 2r+1 across.
					style={{ width: `${(GRAYS.cells.length / ((2 * rings) + 1)) * 100}%` }}
				/>
			</div>

			<div className='custom-color__controls'>
				<div className='custom-color__preview'>
					<RecordIconTile record={{ icon: icon || undefined, color: hex, name: '' }} kind={recordKind} size={56} className='custom-color__preview-tile' />
					<div className='custom-color__preview-text'>
						<div className='custom-color__preview-hex' data-testid='custom-color-hex'>{hex}</div>
						<div className='custom-color__preview-contrast' data-testid='custom-color-contrast'>
							{glyph === IDENTITY_GLYPH_COLORS.light ? 'White' : 'Dark'} icon, {ratioText(ratio)} contrast
						</div>
						{propHex ? null : (
							<div className='custom-color__preview-unset'>No color is chosen yet. Choose one here to use it.</div>
						)}
					</div>
				</div>

				{hsbRow('h', 'Hue', 360, hueTrack)}
				{hsbRow('s', 'Saturation', 100, saturationTrack)}
				{hsbRow('b', 'Brightness', 100, brightnessTrack)}

				<div className='custom-color__typed'>
					<TypedField
						id={field('hex')}
						label='Hex'
						ariaLabel='Hex'
						className='custom-color__hex-field'
						value={hex}
						parse={parseHexInput}
						onCommit={commitRgb}
						problem={problem}
						problemId={problemId}
						setProblem={setProblem}
						problemText={`Hex needs 3 or 6 digits (0 to 9, a to f), like #2f6fed. Keeping ${hex}.`}
						inputMode='text'
						maxLength={16}
					/>
					{channel('r', 'Red')}
					{channel('g', 'Green')}
					{channel('b', 'Blue')}
				</div>

				<div className='custom-color__problem' id={problemId} role='status' aria-live='polite'>
					{problem ? problem.text : ''}
				</div>
			</div>
		</div>
	);
}
